import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Filter,
  Plus,
  MoreVertical,
  Eye,
  UserCheck,
  UserX,
  ShieldOff,
  Users,
  Calendar,
  Mail,
  Phone,
  Download,
  RefreshCw
} from 'lucide-react';
import userService from '../../../../services/userService';
import userRoleService from '../../../../services/userRoleService'; // Add this import
import './UserManagement.css';

const UserList = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<any[]>([]);
  const [userRoles, setUserRoles] = useState<any[]>([]); // Add state for roles
  const [loading, setLoading] = useState(true);
  const [rolesLoading, setRolesLoading] = useState(true); // Add loading for roles
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    user_type: '',
    is_active: '',
    role_id: '',
    account_status: ''
  });
  const [appliedFilters, setAppliedFilters] = useState({ ...filters });
  const [showFilters, setShowFilters] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 5,
    total: 0,
    total_pages: 1
  });
  const [stats, setStats] = useState({
    total_users: 0,
    internal_users: 0,
    dccp_users: 0,
    spokesly_users: 0,
    active_users: 0,
    inactive_users: 0
  });

  // Fetch roles on component mount
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        setRolesLoading(true);
        const response = await userRoleService.getUserRole();
        // Assuming response is an array of role objects
        setUserRoles(response || []);
      } catch (error) {
        console.error('Failed to load user roles:', error);
      } finally {
        setRolesLoading(false);
      }
    };

    fetchRoles();
  }, []);

  // Initial load for users and statistics
  useEffect(() => {
    fetchUsers();
    fetchStatistics();
  }, []);

  const fetchUsers = async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page,
        page_size: pagination.page_size,
        search: search || undefined,
        ...(appliedFilters.user_type && { user_type: appliedFilters.user_type }),
        ...(appliedFilters.is_active && { is_active: appliedFilters.is_active === 'active' }),
        ...(appliedFilters.role_id && { role_id: appliedFilters.role_id }),
        ...(appliedFilters.account_status && { account_status: appliedFilters.account_status })
      };

      const response = await userService.getAllUsers(params);
      setUsers(response.data.data || []);
      setPagination({
        page: response.data.page || 1,
        page_size: response.data.page_size || 5,
        total: response.data.total || 0,
        total_pages: response.data.total_pages || 1
      });
      setStats({
        total_users: response.data.total || 0,
        internal_users: response.data.internal_count || 0,
        dccp_users: response.data.dccp_count || 0,
        spokesly_users: response.data.spokesly_count || 0,
        active_users: response.data.active_count || 0,
        inactive_users: response.data.inactive_count || 0
      });
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await userService.getStatistics();
      setStats(response.data || stats);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  // Get role name by ID
  const getRoleName = (roleId: number) => {
    const role = userRoles.find(r => r.id === roleId);
    return role ? role.name : `Role ${roleId}`;
  };

  // Get role color/class by ID
  const getRoleBadgeClass = (roleId: number) => {
    const role = userRoles.find(r => r.id === roleId);

    // Define badge classes based on role name or type
    if (!role) return 'role-badge';

    const roleName = role.name.toLowerCase();
    if (roleName.includes('admin')) return 'role-badge admin';
    if (roleName.includes('manager')) return 'role-badge manager';
    if (roleName.includes('staff')) return 'role-badge staff';
    if (roleName.includes('dccp')) return 'role-badge dccp';
    if (roleName.includes('spokesly')) return 'role-badge spokesly';

    return 'role-badge';
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    setAppliedFilters({ ...filters });
    setShowFilters(false);
    fetchUsers(1);
  };

  const clearFilters = () => {
    const clearedFilters = {
      user_type: '',
      is_active: '',
      role_id: '',
      account_status: ''
    };
    setFilters(clearedFilters);
    setAppliedFilters(clearedFilters);
    setSearch('');
    setShowFilters(false);
    fetchUsers(1);
  };

  const handleUserAction = async (userId: number, action: string) => {
    try {
      const confirmAction = window.confirm(`Are you sure you want to ${action} this user?`);

      if (confirmAction) {
        if (action === 'delete') {
          await userService.deleteUser(userId);
        } else if (action === 'activate') {
          await userService.activateUser(userId);
        } else if (action === 'deactivate') {
          await userService.deactivateUser(userId);
        } else if (action === 'suspend') {
          await userService.suspendUser(userId);
        }

        fetchUsers();
        fetchStatistics();
        alert(`User ${action}d successfully!`);
      }
    } catch (error) {
      console.error(`Error ${action}ing user:`, error);
      alert(`Failed to ${action} user`);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getUserTypeBadge = (user: any) => {
    const types = {
      internal: { label: 'Internal', class: 'badge-internal' },
      dccp: { label: 'DCCP', class: 'badge-dccp' },
      spokesly: { label: 'Spokesly', class: 'badge-spokesly' }
    };
    const type = types[user.user_type as keyof typeof types] || types.internal;
    return <span className={`badge ${type.class}`}>{type.label}</span>;
  };

  const getStatusBadge = (user: any) => {
    const statuses = {
      active: { label: 'Active', class: 'badge-success' },
      inactive: { label: 'Inactive', class: 'badge-secondary' },
      pending: { label: 'Pending', class: 'badge-warning' },
      suspended: { label: 'Suspended', class: 'badge-danger' },
      blocked: { label: 'Blocked', class: 'badge-danger' }
    };
    const status = statuses[user.account_status as keyof typeof statuses] || statuses.active;
    return <span className={`badge ${status.class}`}>{status.label}</span>;
  };

  return (
    <div className="user-management">
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <h1>User Management</h1>
          <p>Manage all users, roles, and permissions</p>
        </div>
        <div className="header-actions">
          {/* <button
            className="btn btn-primary"
            // onClick={() => navigate('/admin/users/create')}
            onClick={() => alert("User creation is under development.")}
          >
            <Plus size={18} /> Add User
          </button> */}
          {/* <button className="btn btn-secondary">
            <Download size={18} /> Export
          </button> */}
          <button
            className="btn btn-outline"
            onClick={() => {
              fetchUsers();
              fetchStatistics();
            }}
          >
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card total">
          <div className="stat-icon">
            <Users size={24} />
          </div>
          <div className="stat-content">
            <h3>Total Users</h3>
            <div className="stat-value">{stats.total_users}</div>
          </div>
        </div>

        <div className="stat-card active">
          <div className="stat-icon">
            <UserCheck size={24} />
          </div>
          <div className="stat-content">
            <h3>Active Users</h3>
            <div className="stat-value">{stats.active_users}</div>
            <div className="stat-percentage">
              {stats.total_users > 0 ? Math.round((stats.active_users / stats.total_users) * 100) : 0}%
            </div>
          </div>
        </div>

        <div className="stat-card internal">
          <div className="stat-icon">
            <ShieldOff size={24} />
          </div>
          <div className="stat-content">
            <h3>Internal Users</h3>
            <div className="stat-value">{stats.internal_users}</div>
          </div>
        </div>

        <div className="stat-card dccp">
          <div className="stat-icon">
            <UserCheck size={24} />
          </div>
          <div className="stat-content">
            <h3>DCCP Users</h3>
            <div className="stat-value">{stats.dccp_users}</div>
          </div>
        </div>

        <div className="stat-card spokesly">
          <div className="stat-icon">
            <UserCheck size={24} />
          </div>
          <div className="stat-content">
            <h3>Spokesly Users</h3>
            <div className="stat-value">{stats.spokesly_users}</div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="controls-section">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search users by name, email, or phone..."
            value={search}
            onChange={handleSearch}
            className="search-input"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                fetchUsers(1);
              }
            }}
          />
          <button
            className="btn btn-outline btn-sm"
            onClick={() => fetchUsers(1)}
          >
            Search
          </button>
        </div>

        <div className="controls-right">
          <button
            className={`btn-filter ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={18} /> Filters
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="filters-panel">
          <div className="filters-grid">
            <div className="filter-group">
              <label>User Type</label>
              <select
                value={filters.user_type}
                onChange={(e) => handleFilterChange('user_type', e.target.value)}
              >
                <option value="">All Types</option>
                <option value="internal">Internal</option>
                <option value="dccp">DCCP</option>
                <option value="spokesly">Spokesly</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Status</label>
              <select
                value={filters.is_active}
                onChange={(e) => handleFilterChange('is_active', e.target.value)}
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Account Status</label>
              <select
                value={filters.account_status}
                onChange={(e) => handleFilterChange('account_status', e.target.value)}
              >
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="pending">Pending</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Role</label>
              <select
                value={filters.role_id}
                onChange={(e) => handleFilterChange('role_id', e.target.value)}
                disabled={rolesLoading}
              >
                <option value="">All Roles</option>
                {userRoles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
              {rolesLoading && <small className="text-muted">Loading roles...</small>}
            </div>

            <div className="filter-actions">
              <button className="btn btn-outline" onClick={clearFilters}>
                Clear All
              </button>
              <button className="btn btn-primary" onClick={applyFilters}>
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="table-container">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading users...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="empty-state">
            <h3>No users found</h3>
            <p>Try adjusting your search or filters</p>
            <button
              className="btn btn-primary"
              onClick={() => navigate('/admin/users/create')}
            >
              <Plus size={18} /> Add First User
            </button>
          </div>
        ) : (
          <>
            <table className="users-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Contact</th>
                  <th>Role</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Last Login</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td data-label="User">
                      <div className="user-info">
                        <div className="user-details">
                          <strong>{user.first_name} {user.last_name}</strong>
                          <span className="user-email">{user.email}</span>
                          {user.job_title && (
                            <span className="user-job">{user.job_title}</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td data-label="Contact">
                      <div className="contact-info">
                        {user.phone_number && (
                          <div className="contact-item">
                            <Phone size={14} />
                            <span>{user.phone_number}</span>
                          </div>
                        )}
                        <div className="contact-item">
                          <Mail size={14} />
                          <span>{user.email}</span>
                        </div>
                      </div>
                    </td>
                    <td data-label="Role">
                      <span className={getRoleBadgeClass(user.role_id)}>
                        {getRoleName(user.role_id)}
                      </span>
                    </td>
                    <td data-label="Type">{getUserTypeBadge(user)}</td>
                    <td data-label="Status">{getStatusBadge(user)}</td>
                    <td data-label="Last Login">
                      {user.last_login_at ? (
                        <div className="last-login">
                          <Calendar size={14} />
                          <span>{formatDate(user.last_login_at)}</span>
                        </div>
                      ) : (
                        <span className="text-muted">Never</span>
                      )}
                    </td>
                    <td data-label="Actions">
                      <div className="action-button">
                        <button
                          className="btn-icon"
                          onClick={() => navigate(`/admin/users/${user.id}`)}
                          title="View Details"
                        >
                          <Eye size={16} />
                        </button>
                        {/* <div className="dropdown">
                          <button className="btn-icon" title="More Actions">
                            <MoreVertical size={16} />
                          </button>
                          <div className="dropdown-menu" style={{ left: "-130px" }}>
                            {user.is_active ? (
                              <button
                                onClick={() => handleUserAction(user.id, 'deactivate')}
                                className="dropdown-item"
                              >
                                <UserX size={14} /> Deactivate
                              </button>
                            ) : (
                              <button
                                onClick={() => handleUserAction(user.id, 'activate')}
                                className="dropdown-item"
                              >
                                <UserCheck size={14} /> Activate
                              </button>
                            )}
                            <button
                              onClick={() => handleUserAction(user.id, 'suspend')}
                              className="dropdown-item"
                            >
                              <ShieldOff size={14} /> Suspend
                            </button>
                          </div>
                        </div> */}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="pagination">
              <div style={{ display: "flex" }}>
                <button
                  className="pagination-btn"
                  onClick={() => fetchUsers(pagination.page - 1)}
                  disabled={pagination.page === 1}
                >
                  Previous
                </button>

                <div className="pagination-pages">
                  {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                    let pageNum: any;
                    if (pagination.total_pages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.page >= pagination.total_pages - 2) {
                      pageNum = pagination.total_pages - 4 + i;
                    } else {
                      pageNum = pagination.page - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        className={`pagination-page ${pagination.page === pageNum ? 'active' : ''}`}
                        onClick={() => fetchUsers(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  className="pagination-btn"
                  onClick={() => fetchUsers(pagination.page + 1)}
                  disabled={pagination.page === pagination.total_pages}
                >
                  Next
                </button>
              </div>
              <div className="pagination-info">
                Showing {(pagination.page - 1) * pagination.page_size + 1} to{' '}
                {Math.min(pagination.page * pagination.page_size, pagination.total)} of{' '}
                {pagination.total} users
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default UserList;